import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen, AlertCircle } from 'lucide-react';
import { updateProfile } from 'firebase/auth';
import { useAuth } from '../contexts/AuthContext';
import { auth } from '../lib/firebase';

/**
 * Landing screen for the magic-link email. Mounted at /auth/complete.
 *
 * Flow:
 *  1. On mount, attempt to complete sign-in from the URL.
 *  2. If the email isn't in localStorage (link opened on another device),
 *     prompt for it and retry.
 *  3. If a brand-new account has no display name, prompt for the name once,
 *     then finish.
 *  4. Redirect to the stored continue path (or "/").
 */
export default function AuthComplete() {
  const navigate = useNavigate();
  const { isMagicLink, completeMagicLink } = useAuth();

  const [status, setStatus] = useState('working'); // 'working' | 'needEmail' | 'needName' | 'error'
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Guard against React 18 StrictMode double-invocation in dev.
  const attempted = useRef(false);

  const finish = (continuePath) => {
    navigate(continuePath || '/', { replace: true });
  };

  const tryComplete = async (opts = {}) => {
    const { user, continuePath, isNewUser } = await completeMagicLink(opts);

    // New account without a name → ask for it before continuing.
    if (isNewUser && !user.displayName && !opts.displayName) {
      // Stash the continue path so the name step can use it.
      pendingContinue.current = continuePath;
      setStatus('needName');
      return;
    }

    finish(continuePath);
  };

  const pendingContinue = useRef(null);

  useEffect(() => {
    if (attempted.current) return;
    attempted.current = true;

    if (!isMagicLink()) {
      setError('This sign-in link is invalid or has already been used.');
      setStatus('error');
      return;
    }

    tryComplete().catch((err) => {
      if (err.code === 'auth/email-required') {
        setStatus('needEmail');
      } else {
        console.error('Sign-in completion error:', err);
        setError(
          err.code === 'auth/invalid-action-code'
            ? 'This sign-in link has expired or already been used. Please request a new one.'
            : 'We could not sign you in. Please request a new link.'
        );
        setStatus('error');
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      await tryComplete({ email: email.trim() });
    } catch (err) {
      console.error('Sign-in completion error:', err);
      setError('That email did not match this link. Please try again.');
      setStatus('needEmail');
    } finally {
      setSubmitting(false);
    }
  };

  const handleNameSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      // Already signed in at this point; just set the name and continue.
      // Re-running completeMagicLink would fail (link already consumed),
      // so set the profile name directly on the current auth user.
      if (auth.currentUser) {
        await updateProfile(auth.currentUser, { displayName: name.trim() });
      }
      finish(pendingContinue.current);
    } catch (err) {
      console.error('Failed to set name:', err);
      // Non-fatal — let them through anyway.
      finish(pendingContinue.current);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-100 to-white flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 mb-4">
            <div className="w-10 h-10 bg-gradient-to-br from-quiltt-purple to-purple-600 rounded-xl flex items-center justify-center">
              <BookOpen className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-gray-900">Life Story</span>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 md:p-8">
          {status === 'working' && (
            <div className="text-center py-4">
              <div className="w-10 h-10 border-3 border-quiltt-purple border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <p className="text-gray-600">Signing you in...</p>
            </div>
          )}

          {status === 'needEmail' && (
            <>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Confirm your email</h2>
              <p className="text-sm text-gray-500 mb-6">
                To finish signing in, please re-enter the email address you used to
                request this link.
              </p>
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}
              <form onSubmit={handleEmailSubmit}>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  autoComplete="email"
                  autoFocus
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-quiltt-purple focus:border-transparent outline-none mb-4"
                />
                <button
                  type="submit"
                  disabled={submitting || !email.trim()}
                  className="w-full bg-quiltt-purple hover:bg-purple-700 disabled:bg-gray-300 text-white font-medium py-3 px-4 rounded-lg transition-colors"
                >
                  {submitting ? 'Signing in...' : 'Continue'}
                </button>
              </form>
            </>
          )}

          {status === 'needName' && (
            <>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Welcome! What's your name?</h2>
              <p className="text-sm text-gray-500 mb-6">
                This is how you'll appear across Life Story.
              </p>
              <form onSubmit={handleNameSubmit}>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Jane Smith"
                  autoComplete="name"
                  autoFocus
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-quiltt-purple focus:border-transparent outline-none mb-4"
                />
                <button
                  type="submit"
                  disabled={submitting || !name.trim()}
                  className="w-full bg-quiltt-purple hover:bg-purple-700 disabled:bg-gray-300 text-white font-medium py-3 px-4 rounded-lg transition-colors"
                >
                  {submitting ? 'Saving...' : 'Continue'}
                </button>
              </form>
            </>
          )}

          {status === 'error' && (
            <div className="text-center">
              <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
              <h2 className="text-lg font-medium text-gray-900 mb-2">Sign-in failed</h2>
              <p className="text-gray-500 mb-6">{error}</p>
              <button
                onClick={() => navigate('/login', { replace: true })}
                className="text-quiltt-purple hover:underline font-medium"
              >
                Back to sign in
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
